import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { ShieldAlert, ShieldCheck, ArrowRight, X } from 'lucide-react';

const VerificationGateModal = ({ isOpen, onClose, verificationStatus, returnUrl }) => {
  const navigate = useNavigate();

  const handleVerify = () => {
    onClose();
    // P0 FIX: Pass the current vehicle URL as return destination
    // so VerificationPage can show "Return to booking" after upload
    navigate('/verification', { 
      state: { from: returnUrl || window.location.pathname } 
    });
  };

  const isPending = verificationStatus === 'pending';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 top-4 h-8 w-8"
            onClick={onClose}
            data-testid="close-verification-modal"
          >
            <X className="w-4 h-4" />
          </Button>
          <div className="flex flex-col items-center pt-2">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isPending ? 'bg-yellow-100' : 'bg-amber-100'}`}>
              {isPending ? (
                <ShieldCheck className="w-8 h-8 text-yellow-600" />
              ) : (
                <ShieldAlert className="w-8 h-8 text-amber-600" />
              )}
            </div>
            <DialogTitle className="font-heading text-lg text-center">
              {isPending ? 'Verification Pending' : 'Verification Required'}
            </DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="py-4 text-center" data-testid="verification-gate-modal">
          {isPending ? (
            <>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your identity verification is currently being reviewed by our team.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mt-2">
                This usually takes 24-48 hours. We'll notify you once it's complete.
              </p>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-yellow-800 text-sm">
                You'll be able to book vehicles once your verification is approved.
              </div>
            </>
          ) : (
            <>
              <p className="text-slate-600 text-sm leading-relaxed">
                To protect our community, we require identity verification before you can book a vehicle.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mt-2">
                This helps ensure safety for both renters and vehicle hosts.
              </p>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-700 text-sm font-medium mb-2">You'll need:</p>
                <ul className="text-slate-600 text-sm text-left space-y-1">
                  <li>• Driver's licence (front & back)</li>
                  <li>• A selfie for identity matching</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            {isPending ? 'Got it' : 'Maybe Later'}
          </Button>
          {!isPending && (
            <Button 
              onClick={handleVerify}
              className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003865]"
              data-testid="start-verification-btn"
            >
              Start Verification
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationGateModal;
